import { useState } from 'react';
import { AlertCircle, CheckCircle, Mail } from 'lucide-react';

const normalizeWhatsAppNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('62')) return digits;
  if (digits.startsWith('0')) return `62${digits.slice(1)}`;
  if (digits.startsWith('8')) return `62${digits}`;
  return digits;
};

const firstValue = (source, keys) => {
  for (const key of keys) {
    if (source?.[key]) return source[key];
  }
  return '';
};

const normalizeEmployeeName = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const getApproverWhatsApp = (item, step) => {
  const role = step?.key || '';
  const directNumber = firstValue(item, [
    `no_wa_${role}`, `nomor_wa_${role}`, `whatsapp_${role}`,
    `no_hp_${role}`, `telepon_${role}`, 'no_wa_penyetuju',
    'nomor_wa_penyetuju', 'whatsapp_penyetuju', 'no_hp_penyetuju',
  ]);
  if (directNumber) return normalizeWhatsAppNumber(directNumber);

  try {
    const employees = JSON.parse(localStorage.getItem('karyawan_pengganti_list') || '[]');
    if (!Array.isArray(employees)) return '';
    const roleId = firstValue(item, [
      `${role}_id`, `id_${role}`, `karyawan_id_${role}`,
      role === 'pengganti' ? 'pengganti_id' : '',
      'penyetuju_id', 'approval_id',
    ].filter(Boolean));
    const approverNames = [
      role === 'pengganti' ? item.nama_pengganti : '',
      item[`nama_${role}`],
      item.nama_penyetuju,
      step.user,
    ]
      .map(normalizeEmployeeName)
      .filter((name) => name && name !== '-');

    const employee = employees.find((person) =>
      (roleId && String(person.id) === String(roleId))
      || approverNames.includes(normalizeEmployeeName(person.nama))
    );
    return normalizeWhatsAppNumber(firstValue(employee, [
      'no_wa', 'nomor_wa', 'whatsapp', 'no_hp', 'hp', 'telepon', 'phone',
    ]));
  } catch (error) {
    console.warn('Gagal mencari nomor WhatsApp approver', error);
    return '';
  }
};

const getSubmissionLabel = (item) => ({
  kasbon: 'Kasbon',
  cicilan: 'Cicilan',
  lembur: 'Lembur',
  piket_harian: 'Piket Harian',
  piket_perconnote: 'Piket Perconnote',
}[item.absen_id] || item.nama_izin || item.jenis_izin || item.nama || 'Izin/Cuti');

const buildWhatsAppMessage = (item, step, response) => {
  const applicant = item.nama_karyawan || item.nama_pemohon || 'Pemohon';
  const approver = item.nama_penyetuju || step.user || 'Bapak/Ibu';
  const nominal = Number(String(item.nominal || '').replace(/\D/g, ''));
  const details = [
    `Jenis pengajuan: ${getSubmissionLabel(item)}`,
    item.tanggal ? `Tanggal: ${item.tanggal}` : '',
    item.tanggal_mulai ? `Tanggal mulai: ${item.tanggal_mulai}` : '',
    item.tanggal_selesai ? `Tanggal selesai: ${item.tanggal_selesai}` : '',
    nominal ? `Nominal: Rp ${nominal.toLocaleString('id-ID')}` : '',
    item.keperluan ? `Keperluan: ${item.keperluan}` : '',
    item.id ? `ID pengajuan: ${item.id}` : '',
  ].filter(Boolean);

  return [
    `Halo ${approver},`,
    '',
    `Saya ${applicant}. Mohon bantuannya untuk memeriksa dan menyetujui pengajuan berikut:`,
    ...details,
    '',
    `Pesan: ${response.trim()}`,
    '',
    'Terima kasih.',
  ].join('\n');
};

const ApprovalTimelineReadonly = ({ item }) => {
  const isKasbon = item.absen_id === 'kasbon' || item.absen_id === 'cicilan';
  const isPendapatan = item.absen_id === 'lembur' || item.absen_id === 'piket_harian' || item.absen_id === 'piket_perconnote';

  let steps = [];

  if (isKasbon) {
    steps = [
      {
        key: 'pemohon',
        label: 'Pengajuan Pemohon',
        done: Boolean(item.ttd_pemohon),
        time: item.created_at,
        user: item.nama_karyawan,
        catatan: item.catatan,
      },
      {
        key: 'hc',
        label: 'Approval HC',
        done: Boolean(item.ttd_hc),
        time: item.updated_at,
        user: item.nama_penyetuju || 'Pihak HC',
        catatan: item.catatan,
      },
      {
        key: 'direksi',
        label: 'Approval Direksi',
        done: Boolean(item.ttd_direksi),
        time: item.updated_at,
        user: 'Direksi',
        catatan: item.catatan,
      },
    ];
  } else if (isPendapatan) {
    // Pengajuan Lembur / Piket
    steps.push({
      key: 'pemohon',
      label: 'Pengajuan Pemohon',
      done: !!item.ttd_pemohon,
      time: item.created_at,
      user: item.nama_karyawan,
      catatan: item.catatan,
    });

    // Approval SPV - Tampilkan jika ttd_direksi bukan "T" (Skip SPV)
    if (item.ttd_direksi !== "T") {
      steps.push({
        key: 'spv',
        label: 'Approval SPV',
        done: !!item.ttd_direksi, // Di localStorage menggunakan key ttd_direksi (petaan dari ttd_spv mysql)
        time: item.waktu_kacab || item.updated_at,
        user: 'Supervisor',
        catatan: item.catatan,
      });
    }

    // Approval HC
    steps.push({
      key: 'hc',
      label: 'Approval HC',
      done: !!item.ttd_hc, // Hijau jika ttd_hc tidak null/empty
      time: item.updated_at,
      user: item.nama_penyetuju || 'Pihak HC',
      catatan: item.catatan,
    });
  } else {
    // Pengajuan Izin / Cuti
    const getEmployeeInfo = () => {
      try {
        let level = (item.level || '').toLowerCase().trim();
        let dept = (item.departement || item.department || '').toLowerCase().trim();

        if (!level || !dept) {
          const storedKaryawan = localStorage.getItem('data_karyawan');
          if (storedKaryawan) {
            const data = JSON.parse(storedKaryawan);
            const actualData = Array.isArray(data) ? data[0] : data;
            if (!level) level = (actualData.level || '').toLowerCase().trim();
            if (!dept) dept = (actualData.departement || actualData.department || '').toLowerCase().trim();
          }
        }
        return { level, dept };
      } catch (e) { }
      return { level: 'staff', dept: '' };
    };

    const { level, dept } = getEmployeeInfo();
    const getStepsFlow = () => {
      try {
        const karyId = String(item.karyawan_id || '');
        const isKoordinatorLeader = level.includes('koordinator') || level.includes('leader');
        const isOperasional = dept === 'operasional';
        const isSpecialId = karyId === '72' || karyId === '475';

        if (isSpecialId) {
          // 6. ID 72 & 475: Pengganti, Kacab/HC
          return [
            { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'updated_at', user: item.nama_pengganti },
            { key: 'kacab', label: 'Approval Kacab/HC', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab', user: 'Kepala Cabang' }
          ];
        } else if (isKoordinatorLeader) {
          if (isOperasional) {
            // 5. Koordinator Ops: Pengganti, SPV, Kacab/HC
            return [
              { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'updated_at', user: item.nama_pengganti },
              { key: 'koordinator', label: 'Approval Supervisor', ttd_key: 'ttd_koordinator', time_key: 'waktu_koordinator', user: 'Supervisor' },
              { key: 'kacab', label: 'Approval Kacab/HC', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab', user: 'Kepala Cabang' }
            ];
          } else {
            // 4. Koordinator Non-Ops: Pengganti, Kacab/HC
            return [
              { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'updated_at', user: item.nama_pengganti },
              { key: 'kacab', label: 'Approval Kacab/HC', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab', user: 'Kepala Cabang' }
            ];
          }
        } else {
          // Staff
          if (isOperasional) {
            // 3. Staff Ops: Pengganti, Koordinator, SPV
            return [
              { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'updated_at', user: item.nama_pengganti },
              { key: 'koordinator', label: 'Approval Koordinator', ttd_key: 'ttd_koordinator', time_key: 'waktu_koordinator', user: 'Koordinator' },
              { key: 'kacab', label: 'Approval Supervisor', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab', user: 'Supervisor' }
            ];
          } else {
            // 2. Staff Non-Ops: Pengganti, Koordinator, Kacab/HC
            return [
              { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'updated_at', user: item.nama_pengganti },
              { key: 'koordinator', label: 'Approval Koordinator', ttd_key: 'ttd_koordinator', time_key: 'waktu_koordinator', user: 'Koordinator' },
              { key: 'kacab', label: 'Approval Kacab/HC', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab', user: 'Kepala Cabang' }
            ];
          }
        }
      } catch (e) {
        return [
          { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'updated_at', user: item.nama_pengganti },
          { key: 'kacab', label: 'Approval Kacab/HC', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab', user: 'Kepala Cabang' }
        ];
      }
    };

    const flow = getStepsFlow();

    steps = [
      {
        key: 'pemohon',
        label: 'Pengajuan Pemohon',
        done: Boolean(item.ttd_pemohon),
        time: item.created_at,
        user: item.nama_karyawan,
        catatan: item.catatan,
      },
      ...flow.map(f => ({
        key: f.key,
        label: f.label,
        done: Boolean(item[f.ttd_key]),
        time: item[f.time_key],
        user: f.user,
        catatan: item.catatan,
      }))
    ];

  }



  const [tanggapan, setTanggapan] = useState('');
  const activeIndex = steps.findIndex(s => !s.done);
  const activeStep = steps[activeIndex];

  const handleSendTanggapan = (e) => {
    e.stopPropagation();

    if (!tanggapan.trim()) {
      alert('Mohon isi pesan WhatsApp terlebih dahulu');
      return;
    }

    if (!activeStep) return;

    const phone = getApproverWhatsApp(item, activeStep);
    if (!phone) {
      alert(`Nomor WhatsApp ${item.nama_penyetuju || activeStep.user || 'approver'} belum tersedia.`);
      return;
    }

    const message = buildWhatsAppMessage(item, activeStep, tanggapan);
    window.location.href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div style={{ paddingLeft: 12 }}>
      {steps.map((step, idx) => {
        const isActive = idx === activeIndex;

        // Parse & Sort Messages
        let parsedNotes = {};
        try {
          if (typeof item.catatan === 'string') parsedNotes = JSON.parse(item.catatan);
          else if (typeof item.catatan === 'object' && item.catatan) parsedNotes = item.catatan;
        } catch (e) { }

        const rawNote = parsedNotes[`catatan_${step.key}`];
        const rawTanggapan = parsedNotes[`tanggapan_untuk_${step.key}`];

        const messages = [];
        const addMsg = (content, type) => {
          if (!content || content === '-') return;
          let text = typeof content === 'object' ? content.text : content;
          let time = typeof content === 'object' ? content.timestamp : '1970-01-01T00:00:00.000Z';
          if (text && text !== '-') messages.push({ type, text, time });
        };
        addMsg(rawNote, 'note');
        addMsg(rawTanggapan, 'tanggapan');
        messages.sort((a, b) => new Date(a.time) - new Date(b.time));

        return (
          <div
            key={step.key}
            style={{
              display: 'flex',
              gap: 12,
              marginLeft: -8,
              marginBottom: 24,
              position: 'relative',
            }}
          >
            {/* line */}
            {idx !== steps.length - 1 && (
              <div
                style={{
                  position: 'absolute',
                  left: 7,
                  top: 24,
                  width: 2,
                  height: '100%',
                  background: '#e5e7eb',
                }}
              />
            )}

            {/* dot */}
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                marginTop: 4,
                background: step.done
                  ? '#22c55e'
                  : isActive
                    ? '#facc15'
                    : '#d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                zIndex: 1,
              }}
            >
              {step.done && <CheckCircle size={14} />}
              {isActive && <AlertCircle size={14} />}
            </div>

            {/* content */}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{step.label}</div>

              {step.time && step.key === 'pemohon' && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  Diajukan {item.catatan_direksi ? `oleh ${item.catatan_direksi}` : ''}
                </div>
              )}

              {isActive && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  Menunggu persetujuan {item.nama_penyetuju || step.user}
                </div>
              )}

              {step.done && step.key !== 'pemohon' && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  Disetujui {step.user}
                </div>
              )}

              {/* Conversation / Riwayat Percakapan */}
              {/* Conversation / Riwayat Percakapan (Sorted) */}
              {messages.length > 0 && (
                <div style={{
                  marginTop: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}>
                  {messages.map((msg, idx) => {
                    const isNote = msg.type === 'note';
                    // Note (Approver) -> Left, Gray
                    // Tanggapan (Requester) -> Right, Blue
                    const align = isNote ? 'flex-start' : 'flex-end';
                    const bubbleColor = isNote ? '#f3f4f6' : '#eff6ff';
                    const borderColor = isNote ? '#e5e7eb' : '#bfdbfe';
                    const textColor = isNote ? '#374151' : '#1e40af';
                    const userLabel = isNote ? step.user : 'Tanggapan Anda';
                    const labelColor = isNote ? '#6b7280' : '#60a5fa';
                    const radius = isNote ? '0 12px 12px 12px' : '12px 0 12px 12px';

                    return (
                      <div key={idx} style={{ display: 'flex', justifyContent: align, width: '100%' }}>
                        <div style={{
                          maxWidth: '85%',
                          padding: '8px 12px',
                          backgroundColor: bubbleColor,
                          borderRadius: radius,
                          border: `1px solid ${borderColor}`,
                          fontSize: 13,
                          color: textColor,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                            <div style={{ fontSize: 10, color: labelColor, fontWeight: 600 }}>{userLabel}</div>
                            {msg.time !== '1970-01-01T00:00:00.000Z' && (
                              <div style={{ fontSize: 9, color: labelColor, opacity: 0.7, marginLeft: 8 }}>
                                {new Date(msg.time).toLocaleString('id-ID', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                              </div>
                            )}
                          </div>
                          {msg.text}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Form Tanggapan (Response Form) - Only for active waiting step */}
              {/* {isActive && ( */}
              {isActive && (
                <div style={{
                  marginTop: 12,
                  padding: 12,
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}>
                  {/* <label style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: 8
                  }}>
                    Beri Tanggapan / Keterangan Tambahan
                  </label> */}
                  <textarea
                    value={tanggapan}
                    onChange={(e) => setTanggapan(e.target.value)}
                    placeholder={`Tulis pesan untuk ${item.nama_penyetuju || step.user}...`}
                    style={{
                      width: '100%',
                      minHeight: 60,
                      padding: 8,
                      fontSize: 13,
                      borderColor: '#d1d5db',
                      borderRadius: 6,
                      marginBottom: 8,
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      disabled={!tanggapan.trim()}
                      style={{
                        backgroundColor: '#25D366',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        fontSize: 12,
                        fontWeight: 500,
                        borderRadius: 6,
                        cursor: tanggapan.trim() ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                      onClick={handleSendTanggapan}
                    >
                      Kirim ke WhatsApp <div style={{ fontSize: 10 }}>➤</div>
                    </button>
                  </div>
                </div>
              )}


            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ApprovalTimelineReadonly;
